import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Calendar, Sparkles, Instagram, Facebook, Linkedin, Twitter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const platforms = [
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  { id: 'twitter', label: 'Twitter/X', icon: Twitter, color: '#000000' },
];

const SocialPublishing = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newPost, setNewPost] = useState({
    content: '',
    platforms: [],
    scheduled_time: '',
  });
  const [aiGenerating, setAiGenerating] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/social-posts?token=${token}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts', error);
      toast.error('Failed to load social posts');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAI = async () => {
    setAiGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/assist?token=${token}`, {
        query: 'Generate an engaging social media post about CRM and sales automation. Keep it under 200 characters and include 2-3 relevant hashtags.',
        context: { type: 'social_media_post' }
      });
      
      setNewPost({ ...newPost, content: response.data.response || response.data.content || '' });
      toast.success('AI content generated!');
    } catch (error) {
      console.error('Failed to generate AI content', error);
      toast.error('Failed to generate content. Please try again.');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAddPost = async (e) => {
    e.preventDefault();
    
    if (newPost.platforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    try {
      const postData = {
        ...newPost,
        scheduled_time: newPost.scheduled_time || null,
        status: newPost.scheduled_time ? 'scheduled' : 'draft',
        owner_id: 'temp',
      };

      const response = await axios.post(`${API}/social-posts?token=${token}`, postData);
      setPosts([response.data, ...posts]);
      toast.success('Social post created successfully');
      setIsAddDialogOpen(false);
      setNewPost({ content: '', platforms: [], scheduled_time: '' });
    } catch (error) {
      console.error('Failed to create post', error);
      toast.error('Failed to create post');
    }
  };

  const togglePlatform = (platformId) => {
    if (newPost.platforms.includes(platformId)) {
      setNewPost({
        ...newPost,
        platforms: newPost.platforms.filter((p) => p !== platformId),
      });
    } else {
      setNewPost({
        ...newPost,
        platforms: [...newPost.platforms, platformId],
      });
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-slate-400',
      scheduled: 'bg-warning',
      published: 'bg-success',
      failed: 'bg-error',
    };
    return colors[status] || 'bg-slate-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="social-loading">
        <p className="text-slate-500">Loading social posts...</p>
      </div>
    );
  }

  return (
    <div data-testid="social-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-heading font-bold text-primary">Social Publishing</h3>
          <p className="text-sm text-slate-500 mt-1">{posts.length} total posts</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary" data-testid="create-post-button">
              <Plus size={16} className="mr-2" />
              Create Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="create-post-dialog">
            <DialogHeader>
              <DialogTitle>Create Social Media Post</DialogTitle>
              <DialogDescription>
                Create and schedule posts across multiple social media platforms
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddPost} className="space-y-4 mt-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="post-content">Content</Label>
                  <Button
                    type="button"
                    onClick={handleGenerateAI}
                    disabled={aiGenerating}
                    size="sm"
                    variant="outline"
                    className="text-accent border-accent"
                    data-testid="ai-generate-button"
                  >
                    <Sparkles size={14} className="mr-1" />
                    {aiGenerating ? 'Generating...' : 'AI Generate'}
                  </Button>
                </div>
                <Textarea
                  id="post-content"
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  placeholder="Write your post content here..."
                  required
                  rows={5}
                  data-testid="post-content-input"
                />
                <p className="text-xs text-slate-500 mt-1">{newPost.content.length} characters</p>
              </div>

              <div>
                <Label className="mb-3 block">Select Platforms</Label>
                <div className="grid grid-cols-2 gap-3">
                  {platforms.map((platform) => {
                    const Icon = platform.icon;
                    const isSelected = newPost.platforms.includes(platform.id);

                    return (
                      <div
                        key={platform.id}
                        onClick={() => togglePlatform(platform.id)}
                        className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`platform-${platform.id}`}
                      >
                        <Checkbox checked={isSelected} />
                        <Icon size={20} style={{ color: platform.color }} />
                        <span className="font-medium text-sm">{platform.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="scheduled-time">Schedule Time (Optional)</Label>
                <Input
                  id="scheduled-time"
                  type="datetime-local"
                  value={newPost.scheduled_time}
                  onChange={(e) => setNewPost({ ...newPost, scheduled_time: e.target.value })}
                  data-testid="scheduled-time-input"
                />
                <p className="text-xs text-slate-500 mt-1">Leave empty to save as draft</p>
              </div>

              <Button type="submit" className="w-full" data-testid="post-submit-button">
                Create Post
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {posts.map((post) => (
          <Card key={post.id} className="hover:shadow-md transition-shadow duration-200" data-testid={`post-card-${post.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {post.platforms.map((platformId) => {
                      const platform = platforms.find((p) => p.id === platformId);
                      if (!platform) return null;
                      const Icon = platform.icon;
                      return (
                        <div
                          key={platformId}
                          className="w-7 h-7 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${platform.color}20` }}
                        >
                          <Icon size={14} style={{ color: platform.color }} />
                        </div>
                      );
                    })}
                  </div>
                  <CardTitle className="text-base font-heading">
                    {post.content.substring(0, 60)}
                    {post.content.length > 60 && '...'}
                  </CardTitle>
                </div>
                <Badge className={`${getStatusBadge(post.status)} text-white text-xs ml-2`} data-testid="post-status-badge">
                  {post.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3">{post.content}</p>
              {post.scheduled_time && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Calendar size={12} />
                  <span>Scheduled: {new Date(post.scheduled_time).toLocaleString('en-US', { timeZone: 'Asia/Dubai', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {posts.length === 0 && (
          <div className="col-span-full text-center py-12" data-testid="posts-empty-state">
            <p className="text-slate-500">No social posts yet. Create your first post to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialPublishing;
